/**
 * natural_controller — the sector's one handle on the NATURAL OBJECT layer (2026-10-07): it works out what the system
 * is made of (system_makeup.ts), builds the three views and stands between them and SpaceModule.
 *
 * A VIEW NEVER TAKES THE SECTOR DOWN. Every call into a view is guarded: a view that throws is switched off for the
 * rest of the visit (once, with a console warning naming the call) and the sector falls back to its old recipe for
 * whatever that view owned. `localStorage atlas:natural = 'off'` builds no layer at all (the sector as it was).
 */
import type * as THREE from 'three/webgpu';
import type { StarSystem } from '../../galaxy.js';
import { systemMakeup, systemStarLabel } from './system_makeup.js';
import { navBodiesOf, type NavBody } from './natural_nav.js';
import type { SystemMakeup } from './natural_types.js';
import type { NaturalEnv, PlanetHandle, PlanetKindLook, SkyView, StarFrame, StarPlace, StarView, WorldsView } from './natural_api.js';
import { createStarView } from './star_view.js';
import { createWorldsView } from './worlds_view.js';
import { createSkyView } from './sky_view.js';

/** The layer's switch: anything but 'off' is on. */
export function naturalOn(): boolean {
  try { return localStorage.getItem('atlas:natural') !== 'off'; } catch { return true; }
}

type ViewName = 'star' | 'worlds' | 'sky';

export class NaturalController {
  readonly makeup: SystemMakeup;
  private star: StarView | null = null;
  private worlds: WorldsView | null = null;
  private sky: SkyView | null = null;
  private readonly faults: string[] = [];
  private readonly nav: { bodies: NavBody[]; move(nowS: number): void };
  private built = false;
  private disposed = false;

  constructor(private readonly env: NaturalEnv, readonly system: StarSystem) {
    this.makeup = systemMakeup(system);
    this.nav = navBodiesOf(system, this.makeup);
    this.nav.move(env.nowS());
    // The Commons Reach keeps its authored sun: no stellar view there.
    if (system.id !== 'sys0') this.star = this.make('star', () => createStarView(env, this.makeup));
    this.worlds = this.make('worlds', () => createWorldsView(env, this.makeup));
    this.sky = this.make('sky', () => createSkyView(env, this.makeup));
  }

  private make<T>(name: ViewName, f: () => T | null): T | null {
    try { return f(); } catch (e) { this.fault(name, 'create', e); return null; }
  }
  private fault(name: ViewName, call: string, e: unknown): void {
    const msg = `${name}.${call}: ${e instanceof Error ? e.message : String(e)}`;
    this.faults.push(msg);
    console.warn(`[natural] ${msg} — the ${name} view is off for this visit`, e);
    const v = this[name];
    this[name] = null as never;
    try { v?.dispose(); } catch { /* already broken */ }
  }

  /** The bodies a pilot can fly to (nav targets, chart markers). The same objects every call; `pos` follows the clock. */
  navBodies(): readonly NavBody[] { return this.nav.bodies; }

  /** One line for the HUD and the chart ("orange dwarf", "red giant with a white dwarf"). */
  get starLabel(): string { return systemStarLabel(this.makeup); }
  get hasStar(): boolean { return !!this.star; }
  /** The reach (m, about the system's star) of what the star view draws BESIDES the star bodies — discs, streams, jets,
   *  shells: volumes a black hole's lens cannot bend (0 = bare stars; the debug view keeps the hole off it). */
  get starPartsReachM(): number {
    try {
      const st = this.star?.state() as { parts?: unknown[]; reachM?: number } | undefined;
      return st && Array.isArray(st.parts) && st.parts.length > 0 && Number.isFinite(st.reachM) ? Math.max(0, Number(st.reachM)) : 0;
    } catch { return 0; }
  }
  /** True = the worlds view draws this system's comets (the sector's sprite-train comets stand down). */
  get drawsComets(): boolean { return this.worlds?.comets === true; }

  /** Top of the sun update. Null = the sector's own star look. */
  starFrame(dt: number): StarFrame | null {
    if (!this.star) return null;
    try {
      const f = this.star.frame(dt, this.env.nowS());
      // A CLAMPED INPUT IS NOT A FINITE ONE: a NaN from a view must never reach a uniform or a light.
      if (!(Number.isFinite(f.radiusM) && f.radiusM > 0 && Number.isFinite(f.emission) && Number.isFinite(f.corona) && Number.isFinite(f.lightScale) && f.rgb.every(Number.isFinite))) throw new Error('a non-finite star frame');
      return f;
    } catch (e) { this.fault('star', 'frame', e); return null; }
  }
  placeStar(p: StarPlace): void {
    if (!this.star) return;
    try { this.star.place(p); } catch (e) { this.fault('star', 'place', e); }
  }

  planetLook(index: number): PlanetKindLook | null {
    if (!this.worlds) return null;
    try { return this.worlds.planetLook(index); } catch (e) { this.fault('worlds', 'planetLook', e); return null; }
  }
  attachPlanet(index: number, h: PlanetHandle): void {
    if (!this.worlds) return;
    try { this.worlds.attachPlanet(index, h); } catch (e) { this.fault('worlds', 'attachPlanet', e); }
  }
  dressRock(field: number, i: number, rock: THREE.Object3D): void {
    if (!this.worlds) return;
    try { this.worlds.dressRock(field, i, rock); } catch (e) { this.fault('worlds', 'dressRock', e); }
  }
  /** Once the sector's own bodies stand. */
  build(rock: THREE.Object3D): void {
    if (this.built || this.disposed) return;
    this.built = true;
    if (this.worlds) { try { this.worlds.build(rock); } catch (e) { this.fault('worlds', 'build', e); } }
  }

  /** Every sector frame, BEFORE the far-body anchors are seated (a moving body mutates its `abs` here). */
  frame(dt: number): void {
    const now = this.env.nowS();
    this.nav.move(now);
    if (this.worlds) { try { this.worlds.frame(dt, now); } catch (e) { this.fault('worlds', 'frame', e); } }
    if (this.sky) { try { this.sky.frame(dt, now); } catch (e) { this.fault('sky', 'frame', e); } }
  }

  state(): Record<string, unknown> {
    const safe = (v: { state(): Record<string, unknown> } | null): Record<string, unknown> | null => { try { return v ? v.state() : null; } catch { return { error: true }; } };
    const m = this.makeup;
    return {
      system: m.systemId, showpiece: m.showpiece, age: m.age, config: m.config, label: this.starLabel,
      primary: m.primary.kind, companion: m.companion?.kind ?? null, distant: m.distant?.kind ?? null,
      planets: m.planets.map((p) => ({ kind: p.kind, moons: p.moons.map((x) => x.kind), evaporating: p.evaporating, aurora: p.aurora })),
      fields: m.fields.map((f) => ({ taxonomy: f.taxonomy, landmark: f.landmark?.kind ?? null })),
      features: m.features.map((f) => f.kind), sky: m.sky.map((s) => s.kind), skyNebula: m.skyNebula ?? 'rule',
      nav: this.nav.bodies.map((b) => ({ name: b.name, kind: b.kind, pos: b.pos.map(Math.round) })),
      star: safe(this.star), worlds: safe(this.worlds), skyView: safe(this.sky), faults: [...this.faults],
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const v of [this.star, this.worlds, this.sky]) { try { v?.dispose(); } catch { /* a view's own teardown */ } }
    this.star = null; this.worlds = null; this.sky = null;
  }
}
